import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { CSSProperties } from "react";
import type { McpAdminGrantView, McpGrantView } from "@shared/mcp/consent";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import {
  GRANT_STATUS_LABELS,
  grantAccessWords,
  grantScopeNames,
  grantStatusDetail,
} from "@/lib/mcp/grantWords";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";

/**
 * A list of MCP grants (ADR 0009 section 4): the person's own on "Connected apps", an instance's or
 * a tenant's on the AI apps page. One row per connection: the app, the instance it reads, who
 * connected it (admin lists), what it may do, when it was connected and last used, whether it
 * still works (and why not), and Disconnect while it does.
 *
 * Times are the reader's display zone, named once by the caller's `ZoneCaption`; a grid time's
 * `title` is the exact time in the same zone, named (`instantHoverTitle`, #1078).
 */

/** A time on one line ("Sep 28, 2026, 08:08 AM" plus the cell's padding). */
const TIME_WIDTH = 200;
const ACTIONS_WIDTH = 136;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  clientName: 120,
  instanceName: 140,
  user: 200,
  scopes: 136,
  status: 180,
};

type Row = McpGrantView | McpAdminGrantView;

function hasUser(row: Row): row is McpAdminGrantView {
  return "user" in row;
}

export function McpGrantsGrid({
  rows,
  showInstance,
  showUser,
  canRevoke,
  onRevoke,
  ariaLabel,
  emptyMessage,
  testId,
}: {
  rows: Row[];
  showInstance: boolean;
  showUser: boolean;
  canRevoke: boolean;
  onRevoke: (row: Row) => void;
  ariaLabel: string;
  emptyMessage: string;
  testId: string;
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<Row>[] = [
    {
      key: "clientName",
      header: "App",
      width: "20%",
      renderCell: (_value, row) => (
        <SkyText size={300} weight="semibold" block testId={`${testId}-app-${row.id}`}>
          {row.clientName}
        </SkyText>
      ),
    },
    ...(showInstance
      ? [
          {
            key: "instanceName",
            header: "Instance",
            width: "20%",
            renderCell: (_value: unknown, row: Row) => (
              <SkyText size={200} block testId={`${testId}-instance-${row.id}`}>
                {row.instanceName}
              </SkyText>
            ),
          } satisfies SkyDataGridColumn<Row>,
        ]
      : []),
    ...(showUser
      ? [
          {
            key: "user",
            header: "Connected by",
            width: "25%",
            renderCell: (_value: unknown, row: Row) =>
              hasUser(row) ? (
                <span className="flex min-w-0 flex-col">
                  <SkyText size={200} block>
                    {row.user.displayName ?? row.user.email}
                  </SkyText>
                  {row.user.displayName ? (
                    <SkyText size={200} color="muted" block className="break-all">
                      {row.user.email}
                    </SkyText>
                  ) : null}
                </span>
              ) : null,
          } satisfies SkyDataGridColumn<Row>,
        ]
      : []),
    {
      key: "scopes",
      header: "Access",
      width: "10%",
      renderCell: (_value, row) => (
        <span className="flex min-w-0 flex-col">
          <SkyText size={200} block testId={`${testId}-access-${row.id}`}>
            {grantAccessWords(row.scopes)}
          </SkyText>
          <SkyText
            size={200}
            color="muted"
            font="monospace"
            block
            className="break-all"
            testId={`${testId}-scopes-${row.id}`}
          >
            {grantScopeNames(row.scopes)}
          </SkyText>
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Connected",
      width: `${TIME_WIDTH}px`,
      renderCell: (_value, row) => (
        <SkyText size={200} title={instantHoverTitle(row.createdAt, zone)}>
          {formatInstant(row.createdAt, zone, "minutes")}
        </SkyText>
      ),
    },
    {
      key: "lastUsedAt",
      header: "Last used",
      width: `${TIME_WIDTH}px`,
      renderCell: (_value, row) =>
        row.lastUsedAt ? (
          <SkyText size={200} title={instantHoverTitle(row.lastUsedAt, zone)}>
            {formatInstant(row.lastUsedAt, zone, "minutes")}
          </SkyText>
        ) : (
          <SkyText size={200} color="muted">
            Not used yet
          </SkyText>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "25%",
      renderCell: (_value, row) => {
        const detail = grantStatusDetail(row);
        return (
          <span className="flex min-w-0 flex-col items-start gap-1">
            <SkyBadge
              appearance="tint"
              color={row.status === "active" ? "success" : "subtle"}
              testId={`${testId}-status-${row.id}`}
            >
              {GRANT_STATUS_LABELS[row.status]}
            </SkyBadge>
            {detail ? (
              <SkyText size={200} color="muted" block testId={`${testId}-reason-${row.id}`}>
                {detail}
              </SkyText>
            ) : null}
          </span>
        );
      },
    },
    ...(canRevoke
      ? [
          {
            key: "actions",
            header: "Actions",
            width: `${ACTIONS_WIDTH}px`,
            renderCell: (_value: unknown, row: Row) =>
              row.status === "active" ? (
                <SkyButton
                  appearance="secondary"
                  size="small"
                  testId={`${testId}-revoke-${row.id}`}
                  aria-label={`Disconnect ${row.name}`}
                  onClick={() => onRevoke(row)}
                >
                  Disconnect
                </SkyButton>
              ) : null,
          } satisfies SkyDataGridColumn<Row>,
        ]
      : []),
  ];
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    <div style={{ "--mcp-grants-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<Row>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.id}
        {...LIST_GRID_PAGINATION}
        className="[&_table]:min-w-(--mcp-grants-floor)"
        ariaLabel={ariaLabel}
        emptyMessage={emptyMessage}
        testId={testId}
      />
    </div>
  );
}
